import { useEffect, useState, useSyncExternalStore } from 'react';
import { debounce } from 'es-toolkit';

/**
 * ProductContentFrame 카드의 스크롤 영역. 카드 화면에선 창 대신 이 영역이 스크롤하므로 「맨 위로」 버튼이 함께 본다.
 * 카드가 없는 화면에선 `null` 이다.
 */
let scrollContainer: HTMLElement | null = null;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 카드가 자기 스크롤 영역을 알린다. 떼어낼 때는 자기가 등록한 영역일 때만 지운다(화면 전환 중 순서 보호). */
export function setProductContentFrameScrollContainer(
  node: HTMLElement | null,
  previous: HTMLElement | null = null,
) {
  if (!node && scrollContainer !== previous) return;
  scrollContainer = node;
  listeners.forEach(listener => listener());
}

export function getProductContentFrameScrollContainer() {
  return scrollContainer;
}

/** 카드 스크롤 영역의 scrollTop. 카드가 없거나(모바일·외부 플랫폼) 스크롤하지 않으면 0 이다. */
export function useProductContentFrameScrollTop(debounceMs = 100) {
  const container = useSyncExternalStore(
    subscribe,
    () => scrollContainer,
    () => null,
  );
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    if (!container) {
      setScrollTop(0);
      return;
    }
    const handleScroll = debounce(() => {
      setScrollTop(container.scrollTop);
    }, debounceMs);

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      // 떼어낸 카드의 예약된 갱신이 0 으로 되돌린 값을 다시 덮지 않게 한다.
      handleScroll.cancel();
    };
  }, [container, debounceMs]);

  return scrollTop;
}
